/**
 * Etiqueta de severidade do comentário de curadoria: `[SEVERIDADE: ok]` ou
 * `[SEVERIDADE: <emitida>→<devida>]`.
 *
 * O formato é o mesmo que a skill de curadoria grava (`sr_curate.py`) e que o relatório de
 * severidade agrega (`sr_severity.py`, no repositório smart-review). A tela monta a etiqueta
 * a partir de um campo, para que a curadoria feita aqui entre na mesma matriz emitida × devida
 * — sem ela o golden set diz se o achado procede, mas não se o nível estava certo.
 */

/**
 * Severidade em que o achado deveria ter saído. `nada` (procede, mas não deveria ter saído)
 * existe no formato porque a skill aceita em `low_value`, mas não é opção da tela: a tela só
 * pergunta a devida em "severidade errada", onde `nada` seria `low_value` mal classificado.
 */
export type DueSeverity = 'ALTA' | 'MEDIA' | 'sugestao' | 'nada';

export const DUE_SEVERITY_OPTIONS: { value: DueSeverity; label: string }[] = [
  { value: 'ALTA', label: 'Alta' },
  { value: 'MEDIA', label: 'Média' },
  { value: 'sugestao', label: 'Sugestão' }
];

const TAG = /\s*\[SEVERIDADE:\s*([^\]]+)\]/i;
const MOVE = /^(ALTA|MEDIA|BAIXA)\s*(?:→|->)\s*(ALTA|MEDIA|SUGESTAO|NADA)$/i;

/** Normaliza a severidade emitida (o snapshot pode trazer PT ou EN). */
export function emittedSeverity(raw: string | null | undefined): string {
  const value = (raw || '').trim().toUpperCase();
  const map: Record<string, string> = { HIGH: 'ALTA', MEDIUM: 'MEDIA', 'MÉDIA': 'MEDIA', LOW: 'BAIXA' };
  return map[value] || value;
}

/** Comentário sem a etiqueta — o que o curador edita. */
export function stripSeverityTag(comment: string | null | undefined): string {
  return (comment || '').replace(TAG, '').trim();
}

/**
 * Severidade devida lida de um comentário já gravado; `ok` vira a própria emitida.
 * `null` quando não há etiqueta legível.
 */
export function readDueSeverity(comment: string | null | undefined, emitted: string): DueSeverity | null {
  const match = TAG.exec(comment || '');
  if (!match) { return null; }
  const body = match[1].trim().toUpperCase().replace('SUGESTÃO', 'SUGESTAO').replace('MÉDIA', 'MEDIA');
  if (body === 'OK') {
    return emitted === 'ALTA' || emitted === 'MEDIA' ? emitted : null;
  }
  const move = MOVE.exec(body);
  if (!move) { return null; }
  const due = move[2].toUpperCase();
  return due === 'SUGESTAO' ? 'sugestao' : due === 'NADA' ? 'nada' : (due as DueSeverity);
}

/** Monta a etiqueta: `ok` quando a devida é a emitida, `<emitida>→<devida>` quando não. */
export function buildSeverityTag(emitted: string, due: DueSeverity): string {
  return due === emitted ? '[SEVERIDADE: ok]' : `[SEVERIDADE: ${emitted}→${due}]`;
}

/** Comentário final: o texto do curador seguido da etiqueta. */
export function withSeverityTag(comment: string, tag: string): string {
  const text = stripSeverityTag(comment);
  return text ? `${text} ${tag}` : tag;
}
